/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-18
 */

import { TFunction } from 'i18next';

/**
 * Translation-aware validation functions.
 * These functions accept a translation function to provide multilingual error messages.
 */

/**
 * Validates an email address format with translation support.
 * @param email - The email address to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid
 */
export const validateEmailTranslated = (
  email: string,
  t: TFunction
): string | undefined => {
  if (!email) {
    return t('validation.email.required');
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return t('validation.email.invalid');
  }

  return undefined;
};

/**
 * Validates a password meets security requirements with translation support.
 * @param password - The password to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid
 */
export const validatePasswordTranslated = (
  password: string,
  t: TFunction
): string | undefined => {
  if (!password) {
    return t('validation.password.required');
  }

  if (password.length < 8) {
    return t('validation.password.tooShort');
  }

  if (!/[A-Z]/.test(password)) {
    return t('validation.password.needsUppercase');
  }

  if (!/[a-z]/.test(password)) {
    return t('validation.password.needsLowercase');
  }

  if (!/\d/.test(password)) {
    return t('validation.password.needsNumber');
  }

  return undefined;
};

/**
 * Validates a name field with translation support.
 * @param name - The name to validate
 * @param fieldName - The display name of the field for error messages
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid
 */
export const validateNameTranslated = (
  name: string,
  fieldName: string,
  t: TFunction
): string | undefined => {
  if (!name || !name.trim()) {
    return t('validation.name.required', { fieldName });
  }

  return undefined;
};

/**
 * Validates a phone number format with translation support (optional field).
 * @param phone - The phone number to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid or empty
 */
export const validatePhoneTranslated = (
  phone: string,
  t: TFunction
): string | undefined => {
  // Phone is optional, so empty is valid
  if (!phone || !phone.trim()) {
    return undefined;
  }

  const phoneRegex = /^\+?[\d\s-()]+$/;
  if (!phoneRegex.test(phone)) {
    return t('validation.phone.invalid');
  }

  return undefined;
};

/**
 * Validates that password and confirm password fields match with translation support.
 * @param password - The original password
 * @param confirmPassword - The password confirmation
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if passwords don't match, undefined if they match
 */
export const validatePasswordMatchTranslated = (
  password: string,
  confirmPassword: string,
  t: TFunction
): string | undefined => {
  if (!confirmPassword) {
    return t('validation.password.confirmRequired');
  }

  if (password !== confirmPassword) {
    return t('validation.password.noMatch');
  }

  return undefined;
};

/**
 * Validates that a required field has a value with translation support.
 * @param value - The value to validate
 * @param fieldName - The display name of the field for error messages
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if empty, undefined if has value
 */
export const validateRequiredTranslated = (
  value: string,
  fieldName: string,
  t: TFunction
): string | undefined => {
  if (!value || !value.trim()) {
    return t('validation.required.field', { fieldName });
  }

  return undefined;
};

/**
 * Validates a URL format with translation support (optional field).
 * @param url - The URL to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid or empty
 */
export const validateUrlTranslated = (
  url: string,
  t: TFunction
): string | undefined => {
  // URL is optional, so empty is valid
  if (!url || !url.trim()) {
    return undefined;
  }

  try {
    const urlObject = new URL(url);
    return urlObject ? undefined : t('validation.url.invalid');
  } catch {
    return t('validation.url.invalid');
  }
};

/**
 * Validates a bio text field with translation support (optional field with length limit).
 * @param bio - The bio text to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid or empty
 */
export const validateBioTranslated = (
  bio: string,
  t: TFunction
): string | undefined => {
  // Bio is optional, so empty is valid
  if (!bio || !bio.trim()) {
    return undefined;
  }

  if (bio.length > 1000) {
    return t('validation.bio.tooLong');
  }

  return undefined;
};

/**
 * Validates a title field with translation support (optional field).
 * @param title - The title to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid or empty
 */
export const validateTitleTranslated = (
  title: string,
  t: TFunction
): string | undefined => {
  // Title is optional, so empty is valid
  if (!title || !title.trim()) {
    return undefined;
  }

  if (title.length > 100) {
    return t('validation.title.tooLong');
  }

  return undefined;
};

/**
 * Validates a company field with translation support (optional field).
 * @param company - The company name to validate
 * @param t - Translation function from useTranslation('errors')
 * @returns Error message if invalid, undefined if valid or empty
 */
export const validateCompanyTranslated = (
  company: string,
  t: TFunction
): string | undefined => {
  // Company is optional, so empty is valid
  if (!company || !company.trim()) {
    return undefined;
  }

  if (company.length > 100) {
    return t('validation.company.tooLong');
  }

  return undefined;
};

/**
 * Type definition for validation errors object.
 */
export interface ValidationErrors {
  [fieldName: string]: string;
}

/**
 * Validates address fields with all-or-nothing logic and translation support.
 * If any address field has data, then street_address_1, city, and country are required.
 * @param formData - The complete form data object
 * @param t - Translation function from useTranslation('errors')
 * @returns Object with address field errors, or empty object if valid
 */
export const validateAddressTranslated = (
  formData: any,
  t: TFunction
): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Check if any address field has data
  const hasAnyAddressData =
    formData.streetAddress1?.trim() ||
    formData.streetAddress2?.trim() ||
    formData.city?.trim() ||
    formData.stateProvince?.trim() ||
    formData.postalCode?.trim() ||
    formData.country?.trim();

  // If any address field has data, require the essential fields
  if (hasAnyAddressData) {
    if (!formData.streetAddress1?.trim()) {
      errors.streetAddress1 = t('validation.address.streetRequired');
    }
    if (!formData.city?.trim()) {
      errors.city = t('validation.address.cityRequired');
    }
    if (!formData.country?.trim()) {
      errors.country = t('validation.address.countryRequired');
    }
  }

  return errors;
};
